import { ReactElement, useMemo } from 'react';
import { Typography, Button } from '@mui/material';

import { Nullable } from '~globals/types';
import { useSubscriptionFeatures, useSubscription } from '~hooks/index';
import { isPlanCorporate, isCorporate } from '~utils/plans';

import { ACCESS_ALERT_TEXT } from '../utils';
import AccessFeatureHidden from '../Hidden';
import { AccessFeatureHiddenType } from '../Hidden/types';
import AccessFeatureUpgradePlanButton from '../UpgradePlanButton/Base';

import { AccessFeatureAlertProps } from './types';
import {
  AccessFeatureAlertCard,
  AccessFeatureAlertCardContent,
  AccessFeatureAlertCardActions,
} from './styles';

const AccessFeatureAlert = ({
  validFeature,
  availableToDate,
  gutters = false,
}: AccessFeatureAlertProps): Nullable<ReactElement> => {
  const subscription = useSubscription();
  const {
    validateFeatureAccess,
    validateFeatureAvailableToDate,
    getNextPlanFeature,
  } = useSubscriptionFeatures();

  const isDemo = useMemo(
    () =>
      !validateFeatureAccess(validFeature) &&
      validateFeatureAvailableToDate(availableToDate),
    [
      availableToDate,
      validFeature,
      validateFeatureAccess,
      validateFeatureAvailableToDate,
    ],
  );

  const type = useMemo<AccessFeatureHiddenType>(
    () => (isDemo ? 'notAccess' : 'hasAccess'),
    [isDemo],
  );

  const isLastPlan = useMemo(() => {
    if (!validFeature) {
      const isCurrentPlanCorporate = isPlanCorporate(subscription);

      return isCurrentPlanCorporate;
    }

    const nextPlanName = getNextPlanFeature(validFeature);

    return isCorporate(nextPlanName);
  }, [getNextPlanFeature, subscription, validFeature]);

  return (
    <AccessFeatureHidden
      validFeature={validFeature}
      availableToDate={availableToDate}
      type={type}
      validDemo={false}
    >
      <AccessFeatureAlertCard gutters={gutters}>
        <AccessFeatureAlertCardContent>
          <Typography component="h3" variant="body2" fontWeight="bold">
            {ACCESS_ALERT_TEXT}
          </Typography>

          <AccessFeatureAlertCardActions>
            <AccessFeatureUpgradePlanButton
              validFeature={validFeature}
              variant="contained"
              color="primary"
              size="small"
            />

            {!isLastPlan && (
              <Button
                variant="text"
                color="inherit"
                size="small"
                href="https://routix.io/planes"
                target="_blank"
              >
                Comparar Planes
              </Button>
            )}
          </AccessFeatureAlertCardActions>
        </AccessFeatureAlertCardContent>
      </AccessFeatureAlertCard>
    </AccessFeatureHidden>
  );
};

export default AccessFeatureAlert;
